/**
 * Labor Units Agent - Smart Estimation Orchestrator
 * 
 * Intelligent Flow:
 * ┌─────────────────────────────────────────────────────────────────────────┐
 * │  USER QUERY: "Demo 200' EMT, install transformer and 4 receptacles"    │
 * └────────────────────────────────┬────────────────────────────────────────┘
 *                                  │
 *                                  ▼
 * ┌─────────────────────────────────────────────────────────────────────────┐
 * │  STEP 1: GATHER CONTEXT (Parallel)                                      │
 * │  ├─ Query drawings → equipment, schedules, quantities                  │
 * │  └─ Query specs → material requirements, installation rules            │
 * └────────────────────────────────┬────────────────────────────────────────┘
 *                                  │
 *                                  ▼
 * ┌─────────────────────────────────────────────────────────────────────────┐
 * │  STEP 2: PARSE INTO WORK ITEMS (LLM)                                   │
 * │  User query + context → structured work items                          │
 * │                                                                         │
 * │  Output:                                                                │
 * │  ├─ DEMO: 200' 3/4" EMT                                                │
 * │  ├─ INSTALL: 150kVA transformer [needs_assembly: true]                 │
 * │  └─ INSTALL: 4x 20A receptacles [needs_assembly: true]                 │
 * └────────────────────────────────┬────────────────────────────────────────┘
 *                                  │
 *        ┌─────────────────────────┴─────────────────────────┐
 *        │                                                   │
 *        ▼ DEMO ITEMS                         INSTALL ITEMS ▼
 * ┌──────────────────────┐           ┌──────────────────────────────────────┐
 * │  Pass through as-is  │           │  STEP 3: ASSEMBLY LOOKUP (Parallel)  │
 * │  (direct to NECA)    │           │  For each install item concurrently: │
 * │                      │           │  ├─ Query RSMeans PDF                │
 * │                      │           │  ├─ Get component breakdown          │
 * │                      │           │  └─ Expand with quantities           │
 * └──────────┬───────────┘           └────────────────────┬─────────────────┘
 *            │                                            │
 *            └──────────────────┬─────────────────────────┘
 *                               │
 *                               ▼
 * ┌─────────────────────────────────────────────────────────────────────────┐
 * │  STEP 4: NECA LABOR LOOKUP                                             │
 * │  All items (demo + expanded install components) → labor hours          │
 * │                                                                         │
 * │  Demo items: 40-60% of install labor                                   │
 * │  Install items: full NECA labor units                                  │
 * └────────────────────────────────┬────────────────────────────────────────┘
 *                                  │
 *                                  ▼
 * ┌─────────────────────────────────────────────────────────────────────────┐
 * │  STEP 5: MATERIAL COST SCRAPING (Parallel, 5 concurrent)               │
 * │  Each component → Platt.com search → best match price                  │
 * └─────────────────────────────────────────────────────────────────────────┘
 * 
 * Caching:
 * - RSMeans PDF: Gemini Files API (48h TTL) for assembly lookup
 * - NECA PDF: Gemini Context Cache (48h TTL) for labor units
 */

import { GoogleGenAI } from "@google/genai";
import { supabase } from '../lib/supabase';
import { queryDrawings, DrawingCitation, DrawingQueryResult } from './drawingTextExtractor';
import { querySpecifications, SpecsCitation, SpecsResponse } from './specsAgent';
import { 
  formatComponentsForNECA,
  Assembly,
  MatchedAssembly,
  ExpandedComponent
} from './assemblyService';

// TODO: Route Gemini calls through /api/gemini for production security
// For now, this requires VITE_GEMINI_API_KEY in .env.local
const GEMINI_API_KEY = process.env.REACT_APP_GEMINI_API_KEY || '';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface AssemblyStepMatch {
  step_number: number;
  step_description: string;
  quantity: number | null;
  quantity_unit: string | null;
  matched_component: string;
  measurement: string;
  normal_labor_units: number | null;
  difficult_labor_units: number | null;
  very_difficult_labor_units: number | null;
  unit: string; // M, C, or E
  section: string | null;
  page_number: number | null;
  notes: string | null;
  // Material scraping fields
  material_cost?: number | null;
  material_product_title?: string | null;
  material_product_url?: string | null;
}

export interface AssemblyEstimate {
  project_description: string;
  steps: AssemblyStepMatch[];
  total_labor_normal: number;
  total_labor_difficult: number;
  total_labor_very_difficult: number;
  // Tool responses from context gathering phase (for display)
  drawing_response?: {
    answer: string;
    citations: DrawingCitation[];
  };
  specs_response?: {
    answer: string;
    citations: SpecsCitation[];
  };
  // Matched assemblies from RSMeans PDF
  matched_assemblies?: MatchedAssembly[];
}

// Context sources for labor estimation
export interface LaborEstimationContext {
  drawingPdfUrl?: string;
  specsFiles?: Array<{ id: string; name: string; url: string }>;
  estimationId?: string;
}

// ============================================================================
// SMART ORCHESTRATOR TYPES
// ============================================================================

/**
 * Work Item Type - categorizes the type of electrical work
 */
export type WorkItemType = 'DEMO' | 'INSTALL';

/**
 * Parsed Work Item from user query
 * The Step Parser LLM creates these from the user's natural language request
 */
export interface ParsedWorkItem {
  id: number;
  type: WorkItemType;
  description: string;       // e.g., "200' of 3/4\" EMT conduit"
  component: string;         // e.g., "EMT conduit"
  specification: string;     // e.g., "3/4\""
  quantity: number;          // e.g., 200
  quantity_unit: string;     // e.g., "feet"
  needs_assembly: boolean;   // Whether to lookup assembly (true for complex installs)
  reasoning: string;         // Why this decision was made
}

/**
 * Expanded Work Item after assembly lookup
 * Contains original work item + any expanded components from assemblies
 */
export interface ExpandedWorkItem extends ParsedWorkItem {
  assembly_name?: string;              // Name of matched assembly if applicable
  assembly_page?: number;              // RSMeans page reference
  assembly_reasoning?: string;         // Why this assembly was selected (from specs/drawings context)
  expanded_components?: Array<{        // Breakdown from assembly
    description: string;
    quantity: number;
    unit: string;
    from_assembly: string;
  }>;
}

// ============================================================================
// CACHE MANAGEMENT
// ============================================================================

/**
 * Get NECA file URI (uploaded to Gemini Files API)
 * Uses simpler file URI approach instead of context caching (more reliable on Vercel)
 */
async function getNecaFileUri(onThinkingStep?: (step: any) => void): Promise<string> {
  // Check for active, non-expired file URI in database
  const { data: cacheData, error: cacheError } = await supabase
    .from('neca_cache')
    .select('*')
    .eq('active', true)
    .gt('expires_at', new Date().toISOString())
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle(); // Use maybeSingle to avoid error when 0 rows
  
  if (!cacheError && cacheData && cacheData.gemini_file_uri) {
    console.log('✅ Using cached NECA file URI:', cacheData.gemini_file_uri);
    return cacheData.gemini_file_uri;
  }
  
  // File URI expired or doesn't exist - upload new one
  console.log('🔄 No valid NECA file, uploading to Gemini...');
  onThinkingStep?.({ step: 'Loading NECA labor data...', status: 'starting' });
  
  try {
    // Call API endpoint to upload file
    const apiResponse = await fetch('/api/create-neca-cache', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    });
    
    if (!apiResponse.ok) {
      const errorData = await apiResponse.json().catch(() => ({ error: 'Unknown error' }));
      console.error('❌ API error response:', errorData);
      throw new Error(`Failed to upload NECA: ${errorData.message || errorData.error}`);
    }
    
    const result = await apiResponse.json();
    
    if (!result.geminiFileUri) {
      throw new Error('API did not return a Gemini file URI');
    }
    
    console.log('✅ NECA file uploaded:', result.geminiFileUri);
    onThinkingStep?.({ step: 'NECA data ready', status: 'completed' });
    
    return result.geminiFileUri;
    
  } catch (error: any) {
    console.error('❌ Error uploading NECA:', error);
    onThinkingStep?.({ step: 'Failed to load NECA data', status: 'error' });
    throw new Error(`Failed to upload NECA: ${error.message}`);
  }
}

// ============================================================================
// NECA MANUAL QUERY (Read-only, NOT for estimates)
// ============================================================================

/**
 * Citation from NECA Manual query
 */
export interface NecaCitation {
  pageNumber: number;
  quote: string;
  section?: string;        // e.g., "CONDUIT & RACEWAYS", "WIRING DEVICES"
  analysis?: string;       // Why this quote is relevant
  laborUnits?: {           // Optional - if they asked about specific labor values
    normal?: number;
    difficult?: number;
    veryDifficult?: number;
    unit?: string;         // M, C, or E
  };
}

/**
 * Response from NECA Manual query
 */
export interface NecaQueryResponse {
  answer: string;
  citations: NecaCitation[];
  confidence: 'high' | 'medium' | 'low';
}

/**
 * Query NECA Manual for reference information (NOT for building estimates)
 * 
 * Use this to answer questions like:
 * - "What does NECA say about conduit installation?"
 * - "What labor categories exist for receptacles?"
 * - "What are the NECA guidelines for difficult conditions?"
 * 
 * This is NOT for building estimates - use queryLaborUnits() for that.
 * Calls backend /api/neca-manual-agent for secure Gemini access.
 */
export async function queryNecaManual(
  question: string,
  onThinkingStep?: (step: { step: string; status: 'starting' | 'completed' | 'error'; result?: string }) => void
): Promise<NecaQueryResponse> {
  
  // Get NECA file URI (cached)
  onThinkingStep?.({ step: 'Loading NECA Manual...', status: 'starting' });
  const necaFileUri = await getNecaFileUri(onThinkingStep);
  
  onThinkingStep?.({ step: 'Searching NECA Manual...', status: 'starting' });

  try {
    // Call backend API with streaming
    const response = await fetch('/api/neca-manual-agent', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ question, necaFileUri })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to query NECA manual');
    }

    // Handle SSE streaming response
    const reader = response.body?.getReader();
    if (!reader) throw new Error('No response body');

    const decoder = new TextDecoder();
    let result: NecaQueryResponse | null = null;
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      
      // Process complete SSE events
      const lines = buffer.split('\n\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        
        try {
          const event = JSON.parse(line.slice(6));
          
          switch (event.type) {
            case 'thought':
              console.log('💭 NECA Thought:', event.data);
              onThinkingStep?.({ step: event.data, status: 'starting' });
              break;
            case 'status':
              console.log('📊 NECA Status:', event.data);
              onThinkingStep?.({ step: event.data, status: 'starting' });
              break;
            case 'result':
              result = event.data;
              break;
            case 'error':
              throw new Error(event.data);
            case 'done':
              console.log('✅ NECA stream complete');
              break;
          }
        } catch (e) {
          if (line.includes('"type"')) {
            console.warn('Failed to parse SSE event:', line, e);
          }
        }
      }
    }

    if (!result) {
      throw new Error('No result received from NECA manual agent');
    }
    
    onThinkingStep?.({ step: 'NECA Manual search complete', status: 'completed' });
    
    console.log('✅ NECA Manual query complete:', {
      citationCount: result.citations?.length || 0,
      confidence: result.confidence
    });
    
    return result;
    
  } catch (error: any) {
    console.error('❌ NECA Manual query failed:', error);
    onThinkingStep?.({ step: 'Error searching NECA Manual', status: 'error', result: error.message });
    throw error;
  }
}

/**
 * Get RSMeans file URI (uploaded to Gemini Files API)
 * Uses simpler file URI caching instead of context caching (works better for large PDFs)
 */
async function getRSmeansFileUri(onThinkingStep?: (step: any) => void): Promise<string> {
  // Check for active, non-expired file URI in database
  const { data: cacheData, error: cacheError } = await supabase
    .from('rsmeans_cache')
    .select('*')
    .eq('active', true)
    .gt('expires_at', new Date().toISOString())
    .order('created_at', { ascending: false })
    .limit(1)
    .single();
  
  if (!cacheError && cacheData && cacheData.gemini_file_uri) {
    console.log('✅ Using cached RSMeans file URI:', cacheData.gemini_file_uri);
    return cacheData.gemini_file_uri;
  }
  
  // File URI expired or doesn't exist - upload new one
  console.log('🔄 No valid RSMeans file, uploading to Gemini...');
  onThinkingStep?.({ step: 'Loading assembly data...', status: 'starting' });
  
  try {
    const apiResponse = await fetch('/api/create-rsmeans-cache', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    });
    
    if (!apiResponse.ok) {
      const errorData = await apiResponse.json().catch(() => ({ error: 'Unknown error' }));
      console.error('❌ RSMeans API error response:', errorData);
      throw new Error(`Failed to upload RSMeans: ${JSON.stringify(errorData)}`);
    }
    
    const result = await apiResponse.json();
    
    if (!result.geminiFileUri) {
      throw new Error('API did not return a Gemini file URI');
    }
    
    console.log('✅ RSMeans file uploaded:', result.geminiFileUri);
    onThinkingStep?.({ step: 'Assembly data ready', status: 'completed' });
    
    return result.geminiFileUri;
    
  } catch (error: any) {
    console.error('❌ Error uploading RSMeans:', error);
    onThinkingStep?.({ step: 'Failed to load assembly data', status: 'error' });
    throw new Error(`Failed to upload RSMeans: ${error.message}`);
  }
}

// ============================================================================
// CALCULATION FUNCTIONS
// ============================================================================

/**
 * Calculate total labor hours based on quantity, labor units, and unit type
 */
function calculateLaborHours(
  quantity: number | null,
  laborUnits: number | null,
  unitType: string
): number {
  if (!quantity || !laborUnits) return 0;
  
  const unit = unitType.toUpperCase();
  
  // M = per thousand (e.g., 1000 feet of wire)
  if (unit === 'M') {
    return (quantity / 1000) * laborUnits;
  }
  
  // C = per hundred (e.g., 100 receptacles)
  if (unit === 'C') {
    return (quantity / 100) * laborUnits;
  }
  
  // E = each (e.g., one panel)
  if (unit === 'E') {
    return quantity * laborUnits;
  }
  
  // Default to "each" for unknown types
  return quantity * laborUnits;
}

/**
 * Calculate total labor for entire estimate
 */
function calculateTotalLabor(estimate: AssemblyEstimate): AssemblyEstimate {
  let totalNormal = 0;
  let totalDifficult = 0;
  let totalVeryDifficult = 0;
  
  estimate.steps.forEach(step => {
    totalNormal += calculateLaborHours(
      step.quantity,
      step.normal_labor_units,
      step.unit
    );
    totalDifficult += calculateLaborHours(
      step.quantity,
      step.difficult_labor_units,
      step.unit
    );
    totalVeryDifficult += calculateLaborHours(
      step.quantity,
      step.very_difficult_labor_units,
      step.unit
    );
  });
  
  return {
    ...estimate,
    total_labor_normal: totalNormal,
    total_labor_difficult: totalDifficult,
    total_labor_very_difficult: totalVeryDifficult
  };
}

// ============================================================================
// STEP 2: WORK ITEM PARSER (Smart Orchestrator Brain)
// ============================================================================

/**
 * Parse user query + context into structured work items
 * This is the "brain" of the orchestrator - decides what work needs to be done
 * and whether each item needs assembly lookup
 */
async function parseWorkItems(
  userQuery: string,
  drawingContext: string,
  specsContext: string,
  onThinkingStep?: (step: any) => void
): Promise<ParsedWorkItem[]> {
  const client = new GoogleGenAI({ apiKey: GEMINI_API_KEY });
  
  onThinkingStep?.({ step: 'Analyzing work items...', status: 'starting' });
  
  const prompt = `You are an expert electrical estimator. Parse the user's request into individual work items.

USER REQUEST: ${userQuery}

PROJECT CONTEXT:
${drawingContext || '(No drawing data available)'}
${specsContext || '(No specs data available)'}

For each piece of work mentioned, create a work item. Be thorough - extract ALL items.

CATEGORIZATION RULES:

1. DEMO (Demolition/Removal):
   - Removing, demolishing, disconnecting existing equipment
   - "Demo", "remove", "tear out", "disconnect", "decommission"
   - needs_assembly: false (demo never needs assembly lookup)

2. INSTALL (New Installation):
   - Installing, adding, connecting new equipment
   - "Install", "add", "run", "pull", "mount", "connect"
   - needs_assembly: ALWAYS TRUE for ALL installation work
   - Every install item should look up the RSMeans assembly to get:
     * The complete component breakdown (main item + supporting items)
     * Correct quantities for fittings, hardware, wire
     * Reference page numbers for verification
   - Even "simple" items like receptacles have assembly components (box, cover, wire pigtails, etc.)

QUANTITY EXTRACTION:
- Always extract quantities from user query or context
- Use context clues: "4 receptacles", "200 feet of conduit", "qty 3 panels"
- If quantity unclear, use 1 as default

EXAMPLES:

Input: "Demo 200' of 3/4 EMT, install new 150kVA transformer and 4 20A receptacles"

Output work items:
1. DEMO: 200' 3/4" EMT conduit - needs_assembly: false (just removal)
2. INSTALL: 150kVA transformer - needs_assembly: true (assembly includes anchors, conduit, wire, fittings)
3. INSTALL: 4x 20A receptacles - needs_assembly: true (assembly includes box, cover, connections)

Input: "Install 200' of 3/4 EMT and 4 receptacles"

Output work items:
1. INSTALL: 200' 3/4" EMT conduit - needs_assembly: true (assembly includes conduit, fittings, straps, connectors)
2. INSTALL: 4x receptacles - needs_assembly: true (assembly includes box, device, cover plate)

Return a JSON object with a "work_items" array:
{
  "work_items": [
    {
      "id": 1,
      "type": "DEMO",
      "description": "200' of 3/4\" EMT conduit with conductors",
      "component": "EMT conduit",
      "specification": "3/4\"",
      "quantity": 200,
      "quantity_unit": "feet",
      "needs_assembly": false,
      "reasoning": "Demo work - just removal, no assembly needed"
    },
    {
      "id": 2,
      "type": "INSTALL",
      "description": "200' of 3/4\" EMT conduit",
      "component": "EMT conduit",
      "specification": "3/4\"",
      "quantity": 200,
      "quantity_unit": "feet",
      "needs_assembly": true,
      "reasoning": "All installs need assembly - includes conduit, fittings, straps, connectors"
    },
    {
      "id": 3,
      "type": "INSTALL",
      "description": "4x 20A duplex receptacles",
      "component": "Receptacle",
      "specification": "20A, duplex",
      "quantity": 4,
      "quantity_unit": "each",
      "needs_assembly": true,
      "reasoning": "All installs need assembly - includes device, box, cover, wire connections"
    }
  ]
}

IMPORTANT: ALL INSTALL items MUST have needs_assembly: true. Only DEMO items have needs_assembly: false.`;

  try {
    const responseSchema = {
      type: "object",
      properties: {
        work_items: {
          type: "array",
          items: {
            type: "object",
            properties: {
              id: { type: "integer", description: "Sequential ID" },
              type: { type: "string", enum: ["DEMO", "INSTALL"], description: "Type of work" },
              description: { type: "string", description: "Full description of the work item" },
              component: { type: "string", description: "Main component type" },
              specification: { type: "string", description: "Size/spec of component" },
              quantity: { type: "number", description: "Quantity needed" },
              quantity_unit: { type: "string", description: "Unit (each, feet, etc.)" },
              needs_assembly: { type: "boolean", description: "Whether to lookup assembly" },
              reasoning: { type: "string", description: "Why this decision was made" }
            },
            required: ["id", "type", "description", "component", "specification", "quantity", "quantity_unit", "needs_assembly", "reasoning"]
          }
        }
      },
      required: ["work_items"]
    };

    const response = await client.models.generateContent({
      model: 'gemini-2.5-flash',
      contents: prompt,
      config: {
        responseMimeType: 'application/json',
        responseSchema,
        temperature: 0.2
      }
    });

    const result = JSON.parse(response.text || '{"work_items":[]}');
    const workItems: ParsedWorkItem[] = result.work_items || [];
    
    const demoCount = workItems.filter(w => w.type === 'DEMO').length;
    const installCount = workItems.filter(w => w.type === 'INSTALL').length;
    const assemblyCount = workItems.filter(w => w.needs_assembly).length;
    
    console.log('📋 Parsed work items:', {
      total: workItems.length,
      demo: demoCount,
      install: installCount,
      needsAssembly: assemblyCount
    });
    
    onThinkingStep?.({ 
      step: `Identified ${workItems.length} work items (${demoCount} demo, ${installCount} install)`, 
      status: 'completed' 
    });
    
    return workItems;
    
  } catch (error) {
    console.error('❌ Failed to parse work items:', error);
    onThinkingStep?.({ step: 'Failed to parse work items', status: 'error' });
    
    // Fallback: create a single install item from the whole query
    return [{
      id: 1,
      type: 'INSTALL',
      description: userQuery,
      component: 'Electrical work',
      specification: '',
      quantity: 1,
      quantity_unit: 'job',
      needs_assembly: true,
      reasoning: 'Fallback - could not parse individual items'
    }];
  }
}

// ============================================================================
// CONTEXT GATHERING PHASE
// ============================================================================

/**
 * Gather context from drawings and specs before NECA lookup
 */
async function gatherContext(
  userQuery: string,
  context: LaborEstimationContext,
  onThinkingStep?: (step: any) => void
): Promise<{
  drawingContext: string;
  specsContext: string;
  drawingResponse: { answer: string; citations: DrawingCitation[] } | null;
  specsResponse: { answer: string; citations: SpecsCitation[] } | null;
}> {
  let drawingContext = '';
  let specsContext = '';
  let drawingResponse: { answer: string; citations: DrawingCitation[] } | null = null;
  let specsResponse: { answer: string; citations: SpecsCitation[] } | null = null;

  // Build a context-gathering question based on user query
  const drawingQuestion = `Based on this request: "${userQuery}"

List ALL equipment, fixtures, devices, panels, conduit runs, wire sizes, and quantities shown on the drawings that are relevant. Include:
- Equipment schedules and panel schedules
- Fixture counts and types
- Device counts (receptacles, switches, etc.)
- Conduit types and sizes mentioned
- Wire sizes and types
- Any quantities or counts shown
- General notes that specify materials or installation requirements

Be thorough - this will be used to build a labor estimate.`;

  const specsQuestion = `Based on this request: "${userQuery}"

Find ALL specifications relevant to electrical installation, including:
- Material specifications (conduit types, wire types, device specs)
- Installation requirements
- Equipment specifications
- Any quantifiable items
- Special installation conditions that affect labor

Be thorough - this will be used to build a labor estimate.`;

  // Gather context in parallel if both sources available
  const contextPromises: Promise<void>[] = [];

  // Query drawings if available
  if (context.drawingPdfUrl) {
    onThinkingStep?.({ step: 'Scanning drawings for equipment...', status: 'starting' });
    
    contextPromises.push(
      queryDrawings(
        context.drawingPdfUrl,
        drawingQuestion,
        (thought) => {
          onThinkingStep?.({ step: thought, status: 'starting' });
        },
        1,
        undefined,
        context.estimationId
      ).then((result: DrawingQueryResult) => {
        drawingContext = `\n\n=== DRAWING ANALYSIS ===\n${result.answer}\n`;
        drawingResponse = {
          answer: result.answer,
          citations: result.citations
        };
        console.log(`📐 Drawing context: ${result.answer.length} chars, ${result.citations.length} citations`);
        onThinkingStep?.({ 
          step: 'Found equipment on drawings', 
          status: 'completed' 
        });
      }).catch((err: Error) => {
        console.warn('⚠️ Drawing query failed:', err);
        onThinkingStep?.({ 
          step: 'Could not scan drawings', 
          status: 'error' 
        });
      })
    );
  }

  // Query specs if available
  if (context.specsFiles && context.specsFiles.length > 0) {
    onThinkingStep?.({ step: 'Searching specifications...', status: 'starting' });
    
    // Query each specs file
    const specsPromises = context.specsFiles.map(async (specsFile) => {
      try {
        const result = await querySpecifications(
          specsFile.url,
          specsQuestion,
          specsFile.id,
          (step) => onThinkingStep?.({ 
            step: step.step, 
            status: step.status 
          })
        );
        
        return {
          answer: result.answer,
          citations: result.citations.map(c => ({
            ...c,
            fileName: specsFile.name,
            fileId: specsFile.id
          }))
        };
      } catch (err) {
        console.warn(`⚠️ Specs query failed for ${specsFile.name}:`, err);
        return null;
      }
    });

    contextPromises.push(
      Promise.all(specsPromises).then((results) => {
        const validResults = results.filter(r => r !== null);
        if (validResults.length > 0) {
          const combinedAnswer = validResults.map(r => r!.answer).join('\n\n');
          const combinedCitations = validResults.flatMap(r => r!.citations);
          
          specsContext = '\n\n=== SPECIFICATIONS ANALYSIS ===\n' + combinedAnswer;
          specsResponse = {
            answer: combinedAnswer,
            citations: combinedCitations
          };
          console.log(`📋 Specs context: ${specsContext.length} chars, ${combinedCitations.length} citations`);
          onThinkingStep?.({ 
            step: 'Found requirements in specifications', 
            status: 'completed' 
          });
        }
      })
    );
  }

  // Wait for all context gathering to complete
  await Promise.all(contextPromises);

  return { drawingContext, specsContext, drawingResponse, specsResponse };
}

// ============================================================================
// STEP 3: ASSEMBLY MATCHING (RSMeans PDF for Install Items)
// ============================================================================

interface RSMeansComponent {
  description: string;
  quantity: number;
  unit: string;
}

interface RSMeansAssemblyMatch {
  assembly_name: string;
  page_number: number;
  quantity: number;
  run_length: number | null;
  components: RSMeansComponent[];
}

/**
 * Holistic Assembly Estimator
 * Takes ALL work items at once and creates a complete estimate like a professional estimator
 * This avoids double-counting and ensures related items are coordinated
 */
interface HolisticAssemblyResult {
  assemblies: Array<{
    name: string;
    page_number: number;
    reasoning: string;
    for_work_items: number[];  // Which work item IDs this assembly covers
    components: Array<{
      description: string;
      quantity: number;
      unit: string;
    }>;
  }>;
  standalone_components: Array<{
    description: string;
    quantity: number;
    unit: string;
    for_work_item: number;
    reasoning: string;
  }>;
}

async function lookupAssembliesForJob(
  workItems: ParsedWorkItem[],
  drawingContext: string,
  specsContext: string,
  rsmeansFileUri: string,
  onThinkingStep?: (step: any) => void
): Promise<HolisticAssemblyResult> {
  const client = new GoogleGenAI({ apiKey: GEMINI_API_KEY });
  
  // Build work items list for the prompt
  const workItemsList = workItems
    .filter(w => w.type === 'INSTALL')
    .map(w => `  ${w.id}. ${w.description} (${w.quantity} ${w.quantity_unit})`)
    .join('\n');
  
  const demoItemsList = workItems
    .filter(w => w.type === 'DEMO')
    .map(w => `  ${w.id}. DEMO: ${w.description} (${w.quantity} ${w.quantity_unit})`)
    .join('\n');

  const prompt = `You are an expert electrical estimator doing a material takeoff. Review the FULL job scope and create a COMPLETE material list using RSMeans assemblies.

=== FULL JOB SCOPE ===

DEMOLITION ITEMS (no assemblies needed, just note them):
${demoItemsList || '(None)'}

INSTALLATION ITEMS (need assemblies/components):
${workItemsList || '(None)'}

PROJECT CONTEXT:
${drawingContext || '(No drawing data)'}
${specsContext || '(No specs data)'}

=== ESTIMATOR INSTRUCTIONS ===

Think like a professional electrical estimator:

1. **UNDERSTAND THE SCOPE HOLISTICALLY**
   - If user says "200' EMT AND 4 receptacles" - the EMT serves those receptacles
   - Don't add conduit to receptacle assemblies if conduit is already listed separately
   - Understand the RELATIONSHIP between items

2. **SELECT APPROPRIATE ASSEMBLIES**
   - Use RSMeans assemblies from the PDF where they match
   - Match EXACT material types (EMT = EMT, not IMC or rigid)
   - Match EXACT sizes (3/4" = 3/4", not 1/2")

3. **INCLUDE ALL SUPPORTING MATERIALS**
   For conduit runs, always include:
   - Conduit (per foot)
   - Straps/supports (1 per 7-10 feet per NEC)
   - Couplings (1 per 10' stick)
   - Connectors (at boxes)
   - Elbows/fittings (allowance for turns)
   
   For devices (receptacles/switches), include:
   - Device itself
   - Box (4" square or appropriate)
   - Cover plate or plaster ring
   - Wire pigtails if needed (but NOT homerun conduit if already in scope)

4. **AVOID DOUBLE-COUNTING**
   - If conduit run is listed, receptacle assemblies should NOT add more conduit
   - If wire is part of conduit assembly, don't add it again for devices
   - Track which work items each assembly/component serves

5. **PROVIDE CLEAR REASONING**
   - Explain WHY you chose each assembly
   - Note any code requirements (NEC spacing, fill, etc.)
   - Cite specs/drawings context when relevant

=== RESPONSE FORMAT ===

Return JSON with:
- "assemblies": RSMeans assemblies used (with page numbers)
- "standalone_components": Individual components not part of an assembly

{
  "assemblies": [
    {
      "name": "EMT Conduit Assembly, 3/4\" with supports",
      "page_number": 45,
      "reasoning": "200' of 3/4\" EMT conduit per user request. Includes straps every 7' (29 straps), couplings every 10' (20 couplings), and connectors at boxes (8 total for 4 receptacle boxes).",
      "for_work_items": [3],
      "components": [
        { "description": "EMT Conduit, 3/4\"", "quantity": 200, "unit": "L.F." },
        { "description": "EMT Coupling, 3/4\"", "quantity": 20, "unit": "ea" },
        { "description": "EMT Connector, 3/4\"", "quantity": 8, "unit": "ea" },
        { "description": "One-Hole Strap, 3/4\"", "quantity": 29, "unit": "ea" }
      ]
    },
    {
      "name": "Duplex Receptacle Assembly (device only)",
      "page_number": 368,
      "reasoning": "4 receptacles per user request. Using device-only assembly since conduit is provided separately. Includes box, device, and cover.",
      "for_work_items": [4],
      "components": [
        { "description": "4\" Square Box", "quantity": 4, "unit": "ea" },
        { "description": "Duplex Receptacle, 20A", "quantity": 4, "unit": "ea" },
        { "description": "Single Gang Cover Plate", "quantity": 4, "unit": "ea" }
      ]
    }
  ],
  "standalone_components": [
    {
      "description": "THHN Wire, #12, 3 conductors",
      "quantity": 660,
      "unit": "L.F.",
      "for_work_item": 3,
      "reasoning": "3 conductors × 200' run + 10% makeup = 660 L.F. total"
    }
  ]
}`;

  onThinkingStep?.({ step: 'Analyzing job scope for assemblies...', status: 'starting' });

  try {
    const responseSchema = {
      type: "object",
      properties: {
        assemblies: {
          type: "array",
          items: {
            type: "object",
            properties: {
              name: { type: "string", description: "RSMeans assembly name" },
              page_number: { type: "integer", description: "Page in RSMeans PDF" },
              reasoning: { type: "string", description: "Why this assembly was selected" },
              for_work_items: { 
                type: "array", 
                items: { type: "integer" },
                description: "Work item IDs this assembly covers" 
              },
              components: {
                type: "array",
                items: {
                  type: "object",
                  properties: {
                    description: { type: "string" },
                    quantity: { type: "number" },
                    unit: { type: "string" }
                  },
                  required: ["description", "quantity", "unit"]
                }
              }
            },
            required: ["name", "page_number", "reasoning", "for_work_items", "components"]
          }
        },
        standalone_components: {
          type: "array",
          items: {
            type: "object",
            properties: {
              description: { type: "string" },
              quantity: { type: "number" },
              unit: { type: "string" },
              for_work_item: { type: "integer" },
              reasoning: { type: "string" }
            },
            required: ["description", "quantity", "unit", "for_work_item", "reasoning"]
          }
        }
      },
      required: ["assemblies", "standalone_components"]
    };

    const response = await client.models.generateContent({
      model: 'gemini-2.5-flash',
      contents: [
        {
          role: 'user',
          parts: [
            {
              fileData: {
                fileUri: rsmeansFileUri,
                mimeType: 'application/pdf'
              }
            },
            { text: prompt }
          ]
        }
      ],
      config: {
        responseMimeType: 'application/json',
        responseSchema,
        temperature: 0.2
      }
    });

    const result: HolisticAssemblyResult = JSON.parse(
      response.text || '{"assemblies":[],"standalone_components":[]}'
    );
    
    console.log('📦 Holistic assembly result:', {
      assembliesCount: result.assemblies.length,
      standaloneCount: result.standalone_components.length,
      assemblies: result.assemblies.map(a => ({
        name: a.name,
        page: a.page_number,
        components: a.components.length,
        forItems: a.for_work_items
      }))
    });
    
    onThinkingStep?.({ 
      step: `Found ${result.assemblies.length} assemblies, ${result.standalone_components.length} standalone items`, 
      status: 'completed' 
    });
    
    return result;
    
  } catch (error) {
    console.error('❌ Holistic assembly lookup failed:', error);
    onThinkingStep?.({ step: 'Assembly lookup failed', status: 'error' });
    return { assemblies: [], standalone_components: [] };
  }
}

/**
 * Process all work items with HOLISTIC assembly lookup
 * Instead of looking up each item separately, we send the FULL job scope
 * to get a coordinated estimate (like a real estimator would do)
 */
async function processInstallItemsWithAssemblies(
  workItems: ParsedWorkItem[],
  drawingContext: string,
  specsContext: string,
  onThinkingStep?: (step: any) => void
): Promise<ExpandedWorkItem[]> {
  // Check if any items need assembly lookup
  const itemsNeedingAssembly = workItems.filter(w => w.type === 'INSTALL' && w.needs_assembly);
  
  if (itemsNeedingAssembly.length === 0) {
    console.log('📋 No items need assembly lookup');
    return workItems.map(w => ({ ...w }));
  }
  
  onThinkingStep?.({ 
    step: `Analyzing full job scope (${workItems.length} items)...`, 
    status: 'starting' 
  });
  
  // Get RSMeans file URI
  let rsmeansFileUri: string;
  try {
    rsmeansFileUri = await getRSmeansFileUri(onThinkingStep);
  } catch (error) {
    console.error('❌ Failed to get assembly data:', error);
    onThinkingStep?.({ step: 'Assembly data unavailable', status: 'error' });
    return workItems.map(w => ({ ...w }));
  }
  
  // HOLISTIC LOOKUP - send ALL work items at once
  const holisticResult = await lookupAssembliesForJob(
    workItems,
    drawingContext,
    specsContext,
    rsmeansFileUri,
    onThinkingStep
  );
  
  // Convert holistic result to ExpandedWorkItem format
  const expandedItems: ExpandedWorkItem[] = workItems.map(w => {
    // Start with base work item
    const expanded: ExpandedWorkItem = { ...w };
    
    // Find assemblies that cover this work item
    const matchingAssemblies = holisticResult.assemblies.filter(
      a => a.for_work_items.includes(w.id)
    );
    
    // Find standalone components for this work item
    const matchingStandalone = holisticResult.standalone_components.filter(
      c => c.for_work_item === w.id
    );
    
    if (matchingAssemblies.length > 0) {
      // Use the first matching assembly (there should typically be one per work item)
      const assembly = matchingAssemblies[0];
      expanded.assembly_name = assembly.name;
      expanded.assembly_page = assembly.page_number;
      expanded.assembly_reasoning = assembly.reasoning;
      
      // Combine assembly components + standalone components
      const allComponents = [
        ...assembly.components.map(c => ({
          description: c.description,
          quantity: c.quantity,
          unit: c.unit,
          from_assembly: assembly.name
        })),
        ...matchingStandalone.map(c => ({
          description: c.description,
          quantity: c.quantity,
          unit: c.unit,
          from_assembly: `Standalone: ${c.reasoning}`
        }))
      ];
      
      expanded.expanded_components = allComponents.length > 0 ? allComponents : undefined;
    } else if (matchingStandalone.length > 0) {
      // Only standalone components (no assembly matched)
      expanded.assembly_reasoning = matchingStandalone.map(c => c.reasoning).join('; ');
      expanded.expanded_components = matchingStandalone.map(c => ({
        description: c.description,
        quantity: c.quantity,
        unit: c.unit,
        from_assembly: 'Standalone component'
      }));
    }
    
    return expanded;
  });
  
  // Calculate totals for logging
  const totalAssemblies = holisticResult.assemblies.length;
  const totalComponents = holisticResult.assemblies.reduce(
    (sum, a) => sum + a.components.length, 0
  ) + holisticResult.standalone_components.length;
  
  console.log('📦 Holistic assembly processing complete:', {
    workItems: workItems.length,
    assembliesFound: totalAssemblies,
    standaloneComponents: holisticResult.standalone_components.length,
    totalComponents
  });
  
  return expandedItems;
}

/**
 * Legacy function - kept for backwards compatibility
 * Use processInstallItemsWithAssemblies for the new orchestrator flow
 */
async function matchToAssemblies(
  userQuery: string,
  drawingContext: string,
  specsContext: string,
  onThinkingStep?: (step: any) => void
): Promise<{
  matchedAssemblies: MatchedAssembly[];
  assemblyContext: string;
}> {
  const client = new GoogleGenAI({ apiKey: GEMINI_API_KEY });
  
  // Get RSMeans file URI (uploaded to Gemini Files API)
  onThinkingStep?.({ step: 'Loading assembly data...', status: 'starting' });
  
  let rsmeansFileUri: string;
  try {
    rsmeansFileUri = await getRSmeansFileUri(onThinkingStep);
  } catch (error) {
    console.error('❌ Failed to get RSMeans file:', error);
    onThinkingStep?.({ step: 'Assembly data unavailable', status: 'error' });
    return { matchedAssemblies: [], assemblyContext: '' };
  }
  
  onThinkingStep?.({ step: 'Searching for matching assemblies...', status: 'starting' });
  
  const prompt = `Based on the user's request and project context, find ALL matching RSMeans electrical assemblies with their complete component breakdowns.

USER REQUEST: ${userQuery}

PROJECT CONTEXT:
${drawingContext}
${specsContext}

For each piece of equipment identified:
1. Find the matching RSMeans assembly (transformers, services, panels, disconnects, etc.)
2. List ALL components included in that assembly (main equipment, conduit, wire, fittings, anchors, etc.)
3. Include quantities and units for each component
4. Provide page numbers for reference

Return a JSON object with an "assemblies" array:
{
  "assemblies": [
    {
      "assembly_name": "RSMeans assembly description (e.g., 'Transformer, 150kVA, 3P, 480-208Y/120V')",
      "page_number": 123,
      "quantity": 3,
      "run_length": null,
      "components": [
        { "description": "Transformer, 150kVA, 3P, 480-208Y/120V", "quantity": 1, "unit": "ea" },
        { "description": "Wedge Anchor, 1/2\" x 4\"", "quantity": 4, "unit": "ea" },
        { "description": "Conduit, FMC, 2-1/2\"", "quantity": 10, "unit": "ft" },
        { "description": "Wire, Cu THHN, #2/0 (Pri)", "quantity": 30, "unit": "ft" }
      ]
    },
    {
      "assembly_name": "Safety Switch, 200A, 600V, 3P, Fused, NEMA 1",
      "page_number": 145,
      "quantity": 2,
      "run_length": 75,
      "components": [
        { "description": "Safety Switch, 200A, 600V, 3P, Fused", "quantity": 1, "unit": "ea" },
        { "description": "Fuse, 200A, 600V, Class RK1", "quantity": 3, "unit": "ea" }
      ]
    }
  ]
}

MATCHING PRIORITIES:
- Match equipment ratings exactly (KVA for transformers, amps for services/disconnects)
- Match voltage ratings (480V, 208Y/120V, 250V, 600V)
- Match phase configuration (single phase, 3 phase)
- Match enclosure type (NEMA 1, NEMA 3R, etc.)

COMPONENT TYPES TO INCLUDE:
- Main equipment (transformer, switch, panel)
- Mounting hardware (anchors, bolts, channel, straps)
- Conduit and fittings (conduit, connectors, couplings, elbows)
- Wire and cable (phase conductors, neutral, ground/bond)
- Terminations (lugs, connectors)
- Accessories (fuses, ground rods, clamps)

IMPORTANT:
- Include ALL components from the RSMeans assembly, not just main equipment
- Set run_length for feeder/conduit runs (in feet) - per-foot items will be multiplied by this
- If no assemblies match, return {"assemblies": []}`;

  try {
    // Define response schema for structured JSON output (plain JSON, object at root)
    const responseSchema = {
      type: "object",
      properties: {
        assemblies: {
          type: "array",
          description: "List of matched RSMeans assemblies with components",
          items: {
            type: "object",
            properties: {
              assembly_name: { 
                type: "string", 
                description: "RSMeans assembly description" 
              },
              page_number: { 
                type: "integer", 
                description: "Page number in RSMeans PDF" 
              },
              quantity: { 
                type: "integer", 
                description: "Number of this assembly needed" 
              },
              run_length: { 
                type: "integer", 
                nullable: true, 
                description: "Run length in feet if applicable" 
              },
              components: {
                type: "array",
                items: {
                  type: "object",
                  properties: {
                    description: { type: "string" },
                    quantity: { type: "number" },
                    unit: { type: "string" }
                  },
                  required: ["description", "quantity", "unit"]
                }
              }
            },
            required: ["assembly_name", "page_number", "quantity", "components"]
          }
        }
      },
      required: ["assemblies"]
    };

    // Use fileData to include RSMeans PDF in the query
    const response = await client.models.generateContent({
      model: 'gemini-2.5-flash',
      contents: [
        {
          role: 'user',
          parts: [
            {
              fileData: {
                fileUri: rsmeansFileUri,
                mimeType: 'application/pdf'
              }
            },
            {
              text: prompt
            }
          ]
        }
      ],
      config: {
        responseMimeType: 'application/json',
        responseSchema,
        temperature: 0.2
      }
    });
    
    // Parse response - assemblies is now wrapped in an object
    const result = JSON.parse(response.text || '{"assemblies":[]}');
    const parsed: RSMeansAssemblyMatch[] = result.assemblies || [];
    
    if (!Array.isArray(parsed) || parsed.length === 0) {
      console.log('📦 No RSMeans assemblies matched');
      onThinkingStep?.({ step: 'No matching assemblies found', status: 'completed' });
      return { matchedAssemblies: [], assemblyContext: '' };
    }
    
    // Convert RSMeans response to MatchedAssembly format
    const matchedAssemblies: MatchedAssembly[] = parsed.map((match) => {
      const assemblyQty = match.quantity || 1;
      const runLength = match.run_length || undefined;
      
      // Build assembly object
      const assembly: Assembly = {
        name: match.assembly_name,
        components: match.components.map((c) => ({
          quantity: c.quantity,
          quantityUnit: c.unit,
          description: c.description,
          isPerFoot: c.unit === 'ft'
        }))
      };
      
      // Expand components with quantities
      const expandedComponents: ExpandedComponent[] = match.components.map((c) => {
        let quantity = c.quantity * assemblyQty;
        
        // For per-foot items, multiply by run length if provided
        if (c.unit === 'ft' && runLength) {
          // Check if this is a "per foot" item (like wire that scales with run)
          // Assume items already in "ft" scale with run length
          quantity = c.quantity * runLength * assemblyQty;
        }
        
        return {
          description: c.description,
          quantity,
          quantityUnit: c.unit,
          fromAssembly: match.assembly_name
        };
      });
      
      return {
        assembly,
        quantity: assemblyQty,
        runLength,
        expandedComponents
      };
    });
    
    // Format for NECA lookup
    const assemblyContext = formatComponentsForNECA(matchedAssemblies);
    
    const totalComponents = matchedAssemblies.reduce(
      (sum, m) => sum + m.expandedComponents.length, 0
    );
    
    onThinkingStep?.({ 
      step: `Found ${matchedAssemblies.length} assemblies (${totalComponents} components)`, 
      status: 'completed' 
    });
    
    console.log('📦 RSMeans assembly matching complete:', {
      assemblies: matchedAssemblies.length,
      components: totalComponents,
      pages: parsed.map(p => p.page_number)
    });
    
    return { matchedAssemblies, assemblyContext };
    
  } catch (error) {
    console.error('❌ RSMeans assembly matching failed:', error);
    onThinkingStep?.({ step: 'Assembly matching failed', status: 'error' });
    return { matchedAssemblies: [], assemblyContext: '' };
  }
}

// ============================================================================
// STEP 4: NECA LABOR LOOKUP (for all work items)
// ============================================================================

/**
 * Build NECA prompt from expanded work items
 * Creates a structured prompt with all demo items + expanded install components
 */
function buildNecaPromptFromWorkItems(
  userQuery: string,
  expandedWorkItems: ExpandedWorkItem[],
  drawingContext: string,
  specsContext: string
): string {
  // Build work items section
  let workItemsSection = '\n=== WORK ITEMS TO ESTIMATE ===\n';
  
  expandedWorkItems.forEach((item, idx) => {
    workItemsSection += `\n${idx + 1}. ${item.type}: ${item.description}`;
    workItemsSection += `\n   Component: ${item.component}`;
    workItemsSection += `\n   Spec: ${item.specification}`;
    workItemsSection += `\n   Quantity: ${item.quantity} ${item.quantity_unit}`;
    
    if (item.assembly_name) {
      workItemsSection += `\n   [Assembly: ${item.assembly_name}, pg. ${item.assembly_page}]`;
    }
    
    if (item.expanded_components && item.expanded_components.length > 0) {
      workItemsSection += '\n   Expanded Components:';
      item.expanded_components.forEach(comp => {
        workItemsSection += `\n     - ${comp.description}: ${comp.quantity} ${comp.unit}`;
      });
    }
  });
  
  workItemsSection += '\n=== END WORK ITEMS ===\n';
  
  const contextSection = (drawingContext || specsContext) ? `
=== PROJECT CONTEXT ===
${drawingContext}
${specsContext}
=== END PROJECT CONTEXT ===
` : '';

  return `You are an expert electrical estimator. Look up NECA labor units for the work items below.

USER REQUEST: ${userQuery}
${contextSection}
${workItemsSection}

INSTRUCTIONS:

1. For DEMO items: Use 50% of normal install labor (demo is faster than install)
2. For INSTALL items WITH expanded components: Create a step for EACH component
3. For INSTALL items WITHOUT expanded components: Create a single step

For EACH step, you MUST provide:
- step_number: Sequential (1, 2, 3...)
- step_description: "Demo - [component]" or "Install - [component]"
- quantity: From work item or expanded component
- quantity_unit: (feet, each, etc.)
- matched_component: Exact NECA manual component name
- measurement: Size/spec from NECA (e.g., "3/4\"", "20 Amp 3 Wire")
- normal_labor_units: From NECA table (halve for demo)
- difficult_labor_units: From NECA table
- very_difficult_labor_units: From NECA table
- unit: M (per 1000), C (per 100), or E (each)
- page_number: NECA manual page
- section: NECA section name
- notes: Any clarifications

CRITICAL:
- Create SEPARATE steps for each expanded component (wire, conduit, fittings, anchors, etc.)
- DEMO labor = 50% of install labor
- QUANTITY and PAGE_NUMBER are MANDATORY
- Extract EXACT labor values from NECA tables

Return complete JSON with ALL fields populated.`;
}

// ============================================================================
// MAIN QUERY FUNCTION (Smart Orchestrator)
// ============================================================================

/**
 * Query NECA manual to match installation steps and calculate labor
 * 
 * Now calls backend API to keep Gemini API key secure
 * 
 * FLOW:
 * - If fullEstimate = true: Gather context → RSMeans → NECA → Materials
 * - If fullEstimate = false: Skip context/RSMeans → NECA → Materials (lightweight)
 * 
 * @param fullEstimate - Whether to do full estimation flow (default: true for backwards compatibility)
 */
export async function queryLaborUnits(
  userQuery: string,
  onThinkingStep?: (step: any) => void,
  estimationId?: string,
  context?: LaborEstimationContext,
  fullEstimate: boolean = true
): Promise<AssemblyEstimate> {
  
  // ========================================
  // STEP 1: Gather context from drawings & specs (ONLY if fullEstimate)
  // ========================================
  let drawingContext = '';
  let specsContext = '';
  let drawingResponse: { answer: string; citations: DrawingCitation[] } | null = null;
  let specsResponse: { answer: string; citations: SpecsCitation[] } | null = null;

  if (fullEstimate && context && (context.drawingPdfUrl || (context.specsFiles && context.specsFiles.length > 0))) {
    console.log('📐 Full estimate mode: gathering context from drawings/specs...');
    const gathered = await gatherContext(userQuery, context, onThinkingStep);
    drawingContext = gathered.drawingContext;
    specsContext = gathered.specsContext;
    drawingResponse = gathered.drawingResponse;
    specsResponse = gathered.specsResponse;
    
    console.log('📊 Context gathered:', {
      drawingChars: drawingContext.length,
      specsChars: specsContext.length,
      drawingCitations: drawingResponse?.citations.length || 0,
      specsCitations: specsResponse?.citations.length || 0
    });
  } else if (!fullEstimate) {
    console.log('⚡ Simple component mode: skipping context gathering');
  }
  
  // ========================================
  // STEP 2: Call backend API for labor calculation
  // ========================================
  onThinkingStep?.({ step: 'Calculating labor units...', status: 'starting' });
  
  try {
    const response = await fetch('/api/labor-units-agent', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
    userQuery,
    drawingContext,
    specsContext,
    fullEstimate
      })
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(errorData.error || `API error: ${response.status}`);
      }
    
    const estimate = await response.json();
    
    // Attach full responses from context gathering (for UI display)
    if (drawingResponse) {
      estimate.drawing_response = drawingResponse;
    }
    if (specsResponse) {
      estimate.specs_response = specsResponse;
    }
    
    onThinkingStep?.({
      step: `Built estimate: ${estimate.steps?.length || 0} items, ${(estimate.total_labor_normal || 0).toFixed(1)} hours`,
      status: 'completed'
    });
    
    console.log('📊 Estimate complete:', {
      steps: estimate.steps?.length || 0,
      drawingCitations: drawingResponse?.citations.length || 0,
      specsCitations: specsResponse?.citations.length || 0
    });
    
    return estimate;
    
  } catch (error: any) {
    console.error('Error querying labor units:', error);
      onThinkingStep?.({
        step: 'Failed to calculate labor',
        status: 'error'
      });
    throw error;
  }
}

// ============================================================================
// MATERIAL SCRAPING FUNCTION
// ============================================================================

/**
 * Scrape material costs from Platt.com for all labor steps
 * Uses concurrent scraping with controlled concurrency (3 at a time)
 */
async function scrapeMaterialsForSteps(
  estimationId: string,
  steps: AssemblyStepMatch[],
  onThinkingStep?: (step: any) => void
): Promise<void> {
  if (steps.length === 0) return;
  
  onThinkingStep?.({ 
    step: `Scraping material costs for ${steps.length} component(s) from Platt.com (5 concurrent)...`, 
    status: 'starting' 
  });
  
  // Create a scraping task for each step
  const scrapeTasks = steps.map((step, index) => async () => {
    try {
      // Build search query from component + measurement
      const searchQuery = `${step.matched_component} ${step.measurement}`;
      
      onThinkingStep?.({
        step: `[${index + 1}/${steps.length}] Searching: "${searchQuery}"`,
        status: 'starting'
      });
      
      // Call serverless API endpoint
      const response = await fetch('/api/scrape-material', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ searchQuery })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(`API error: ${errorData.error || response.statusText}`);
      }
      
      const result = await response.json();
      
      if (result && result.best_match) {
        // Update database with material info
        const { error } = await supabase
          .from('estimate_labor_steps')
          .update({
            material_cost: result.best_match.price,
            material_product_title: result.best_match.title,
            material_product_url: result.best_match.product_url,
            material_scraped_at: new Date().toISOString()
          })
          .eq('estimation_id', estimationId)
          .eq('matched_component', step.matched_component)
          .eq('measurement', step.measurement);
        
        if (error) {
          console.error(`Failed to update material for ${step.matched_component}:`, error);
          onThinkingStep?.({
            step: `[${index + 1}/${steps.length}] ${step.matched_component}: Failed to save`,
            status: 'error'
          });
        } else {
          onThinkingStep?.({
            step: `[${index + 1}/${steps.length}] ${step.matched_component}: $${result.best_match.price}/unit`,
            status: 'completed',
            result: result.best_match.product_url
          });
        }
      } else {
        onThinkingStep?.({
          step: `[${index + 1}/${steps.length}] ${step.matched_component}: No match found`,
          status: 'error'
        });
      }
      
    } catch (error) {
      console.error(`Error scraping material for ${step.matched_component}:`, error);
      onThinkingStep?.({
        step: `[${index + 1}/${steps.length}] ${step.matched_component}: ${error instanceof Error ? error.message : 'Error'}`,
        status: 'error'
      });
    }
  });
  
  // Run scraping tasks with controlled concurrency (5 at a time)
  const CONCURRENCY_LIMIT = 100;
  const results = [];
  
  for (let i = 0; i < scrapeTasks.length; i += CONCURRENCY_LIMIT) {
    const batch = scrapeTasks.slice(i, i + CONCURRENCY_LIMIT);
    const batchResults = await Promise.allSettled(batch.map(task => task()));
    results.push(...batchResults);
    
    // Small delay between batches to be polite
    if (i + CONCURRENCY_LIMIT < scrapeTasks.length) {
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
  }
  
  const successCount = results.filter(r => r.status === 'fulfilled').length;
  
  onThinkingStep?.({ 
    step: `Material scraping complete (${successCount}/${steps.length} successful)`, 
    status: 'completed' 
  });
}

// ============================================================================
// SUPABASE SAVE FUNCTION
// ============================================================================

/**
 * Save labor estimate to Supabase
 * Trusts the AI's output - if step exists, update it; if new, insert it
 * The AI already has context about existing table and user intent
 * 
 * @param fullEstimate - If false (autocount mode), convert C/M units to E. If true, keep original units.
 */
export async function saveToSupabase(
  estimationId: string,
  estimate: AssemblyEstimate,
  userQuery?: string,
  onThinkingStep?: (step: any) => void,
  fullEstimate: boolean = true
): Promise<void> {
  
  console.log('💾 Saving labor data to Supabase...', {
    estimationId,
    stepsCount: estimate.steps.length,
    totalNormal: estimate.total_labor_normal
  });
  
  try {
    // Step 1: Update estimates table with totals
    const { error: updateError } = await supabase
      .from('estimations')
      .update({
        labor_hours_normal: estimate.total_labor_normal,
        labor_hours_difficult: estimate.total_labor_difficult,
        labor_hours_very_difficult: estimate.total_labor_very_difficult,
        labor_calculation_date: new Date().toISOString()
      })
      .eq('id', estimationId);
    
    if (updateError) {
      console.error('❌ Failed to update estimates table:', updateError);
      throw updateError;
    }
    
    console.log('✅ Updated estimates table with totals');
    
    // Step 2: Replace entire table with AI's output
    // AI already has context about existing table and returns the complete desired state
    console.log('🗑️ Clearing existing steps...');
    
    const { error: deleteError } = await supabase
      .from('estimate_labor_steps')
      .delete()
      .eq('estimation_id', estimationId);
    
    if (deleteError) {
      console.error('❌ Failed to clear existing steps:', deleteError);
      throw deleteError;
    }
    
    // Step 3: Insert all steps from AI (with conversion to per-each basis)
    console.log('📝 Inserting new steps from AI...');
    
    const stepsToInsert = estimate.steps.map((step, index) => {
      // Convert labor units based on mode
      let convertedNormal = step.normal_labor_units;
      let convertedDifficult = step.difficult_labor_units;
      let convertedVeryDifficult = step.very_difficult_labor_units;
      let convertedUnit = step.unit;
      
      if (!fullEstimate) {
        // AUTOCOUNT MODE: Convert C/M to E (per-each) for counting individual symbols
        if (step.unit === 'C') {
          console.log(`🔄 [AUTOCOUNT] Converting ${step.matched_component} from C to E: ${convertedNormal} → ${(convertedNormal || 0) / 100}`);
          convertedNormal = convertedNormal ? convertedNormal / 100 : 0;
          convertedDifficult = convertedDifficult ? convertedDifficult / 100 : 0;
          convertedVeryDifficult = convertedVeryDifficult ? convertedVeryDifficult / 100 : 0;
          convertedUnit = 'E';
        } else if (step.unit === 'M') {
          console.log(`🔄 [AUTOCOUNT] Converting ${step.matched_component} from M to E: ${convertedNormal} → ${(convertedNormal || 0) / 1000}`);
          convertedNormal = convertedNormal ? convertedNormal / 1000 : 0;
          convertedDifficult = convertedDifficult ? convertedDifficult / 1000 : 0;
          convertedVeryDifficult = convertedVeryDifficult ? convertedVeryDifficult / 1000 : 0;
          convertedUnit = 'E';
        } else {
          console.log(`✅ [AUTOCOUNT] ${step.matched_component} already in E units, no conversion needed`);
        }
      } else {
        // FULL ESTIMATE MODE: Keep original unit type (E, C, or M)
        // UI will handle conversion at calculation time
        console.log(`✅ [ESTIMATE] ${step.matched_component} keeping original unit: ${step.unit}`);
      }
      
      return {
        estimation_id: estimationId,
        step_number: index + 1,
        step_description: step.step_description,
        quantity: step.quantity,
        quantity_unit: step.quantity_unit,
        matched_component: step.matched_component,
        measurement: step.measurement,
        normal_labor_units: convertedNormal,
        difficult_labor_units: convertedDifficult,
        very_difficult_labor_units: convertedVeryDifficult,
        unit: convertedUnit,
        section: step.section,
        page_number: step.page_number,
        notes: step.notes
      };
    });
    
    if (stepsToInsert.length > 0) {
      const { error: insertError } = await supabase
        .from('estimate_labor_steps')
        .insert(stepsToInsert);
      
      if (insertError) {
        console.error('❌ Failed to insert labor steps:', insertError);
        throw insertError;
      }
    }
    
    console.log(`💾 Saved ${stepsToInsert.length} steps to database`);
    
    // Step 4: Scrape material costs for all steps
    try {
      await scrapeMaterialsForSteps(estimationId, estimate.steps, onThinkingStep);
    } catch (error) {
      console.error('❌ Material scraping failed (non-fatal):', error);
      onThinkingStep?.({
        step: 'Material scraping failed - you can add costs manually',
        status: 'error'
      });
      // Don't throw - material scraping is optional, labor estimate still works
    }
    
  } catch (error) {
    console.error('❌ Error saving to Supabase:', error);
    throw error;
  }
}

/**
 * Calculate labor for a single step (used by frontend)
 */
export function calculateStepLabor(step: AssemblyStepMatch) {
  return {
    normal: calculateLaborHours(step.quantity, step.normal_labor_units, step.unit),
    difficult: calculateLaborHours(step.quantity, step.difficult_labor_units, step.unit),
    very_difficult: calculateLaborHours(step.quantity, step.very_difficult_labor_units, step.unit)
  };
}

